import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDateWithoutTZ, getSelectedDateRangeString } from '@common/utils';
import { Box, Dropdown, DropdownItem, DropdownVariant } from '@base-app';
import { DateRangeDropdown, DateRangeSelection, DateRangeOptionId } from '@builder';
import { COMMON_TRANSLATION, LAYOUT_THREE } from '@constants/index';
import { useFeatureFlags } from '@modules/feature-flags/hooks';
import { FeatureFlags } from '@modules/feature-flags/types';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { SampleSerieType } from '@modules/study-insights/types';
import { SamplesViewGraphFilterProps } from './SampleViewGraphFilter.types';
import { SampleTypeSelectId } from '../../../Demographics/Demographics.types';
import {
  StyledDateFilterInfo,
  StyledDateRange,
  StyledSamplesViewGraphFilter,
  StyledSelectedRangeText,
  StyledSampleTypeFilterText,
} from './SampleViewGraphFilter.styles';
import { useGoalsStore } from '@modules/study-insights/store';

const SamplesViewGraphFilter: FC<SamplesViewGraphFilterProps> = ({
  displaySampleTypeFilter = false,
  onFilterChange,
  selectedDateRange,
  sampleTypeFilterText,
  excludedSampleTypes = [],
}) => {
  const { t } = useTranslation([STUDY_INSIGHTS_TRANSLATION, COMMON_TRANSLATION]);
  const { isFeatureEnabled } = useFeatureFlags();
  const isBuccalEnabled = isFeatureEnabled(FeatureFlags.BUCCAL_SAMPLE_TYPE);
  const ALL_SAMPLE_TYPE_OPTIONS = [
    {
      id: SampleTypeSelectId.ALL,
      label: t('demographics.all_sample_type'),
    },
    {
      id: SampleTypeSelectId.BLOOD_SAMPLES,
      label: t('demographics.blood_samples'),
    },
    {
      id: SampleTypeSelectId.SALIVA_SAMPLES,
      label: t('demographics.saliva_samples'),
    },
    {
      id: SampleTypeSelectId.BUCCAL_SAMPLES,
      label: t('demographics.buccal_samples'),
    },
  ];
  const SAMPLE_TYPE_OPTIONS = ALL_SAMPLE_TYPE_OPTIONS.filter(
    (option) =>
      !excludedSampleTypes.includes(option.id) && (isBuccalEnabled || option.id !== SampleTypeSelectId.BUCCAL_SAMPLES),
  );

  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeSelection | undefined>(selectedDateRange);
  const [samplesTypeFilter, setSamplesTypeFilter] = useState<SampleSerieType | undefined>();
  const [dateRangeDisplay, setDateRangeDisplay] = useState<string>();
  const [sampleType, setSampleType] = useState<DropdownItem>(SAMPLE_TYPE_OPTIONS[0]);
  const startDateGoal = useGoalsStore((store) => store.lifetimeGoal?.startDate);
  const endDateGoal = useGoalsStore((store) => store.lifetimeGoal?.endDate);

  const handleDateRangeSelect = (dateRangeOption: DateRangeSelection): void => {
    let selectionStartDate = dateRangeOption?.startDate;
    let selectionEndDate = dateRangeOption?.endDate;

    // if filter is All Time, select lifetime start date
    if (dateRangeOption?.selectedDateRange === DateRangeOptionId.ALL_TIME) {
      if (startDateGoal) {
        selectionStartDate = new Date(startDateGoal);
      }
      selectionEndDate = new Date();
    }
    setDateRangeFilter({
      ...dateRangeOption,
      startDate: selectionStartDate,
      endDate: selectionEndDate,
    });
  };

  const handleSampleTypeSelect = (option: DropdownItem): void => {
    setSamplesTypeFilter(option.id as SampleSerieType);
    setSampleType(option);
  };

  useEffect(() => {
    onFilterChange({
      dateRange: dateRangeFilter,
      type: samplesTypeFilter,
    });
  }, [dateRangeFilter, samplesTypeFilter]);

  useEffect(() => {
    if (!selectedDateRange) {
      return;
    }

    const { startDate, endDate, selectedDateRange: dateRangeOption } = selectedDateRange;
    if (dateRangeOption === DateRangeOptionId.ALL_TIME) {
      setDateRangeDisplay(t('date_range_dropdown_options.all_time', { ns: COMMON_TRANSLATION }));
    } else if (startDate !== undefined && endDate !== undefined) {
      setDateRangeDisplay(getSelectedDateRangeString(startDate, endDate));
    }
  }, [selectedDateRange]);

  return (
    <StyledSamplesViewGraphFilter>
      {displaySampleTypeFilter && (
        <Dropdown
          id="sample-view-graph-filter-type"
          variant={DropdownVariant.PILL}
          items={SAMPLE_TYPE_OPTIONS}
          onSelect={handleSampleTypeSelect}
          title={sampleType?.label ?? ''}
        />
      )}
      {!displaySampleTypeFilter && sampleTypeFilterText && (
        <StyledSampleTypeFilterText>{sampleTypeFilterText}</StyledSampleTypeFilterText>
      )}
      <StyledDateFilterInfo>
        <StyledSelectedRangeText>
          <Box>{t('selected_date_range.label')}</Box>
          <StyledDateRange>{dateRangeDisplay}</StyledDateRange>
        </StyledSelectedRangeText>
        <DateRangeDropdown
          value={selectedDateRange}
          dateRangeErrorTitle={t('selected_date_range.error.title')}
          dateRangeErrorDescription={t('selected_date_range.error.description', {
            dateRange:
              startDateGoal &&
              endDateGoal &&
              getSelectedDateRangeString(
                new Date(getDateWithoutTZ(startDateGoal)),
                new Date(getDateWithoutTZ(endDateGoal)),
              ),
          })}
          maxDate={endDateGoal ? new Date(getDateWithoutTZ(endDateGoal)) : undefined}
          minDate={startDateGoal ? new Date(getDateWithoutTZ(startDateGoal)) : undefined}
          onSelect={handleDateRangeSelect}
          layout={LAYOUT_THREE}
        />
      </StyledDateFilterInfo>
    </StyledSamplesViewGraphFilter>
  );
};

export default SamplesViewGraphFilter;
